import { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Briefcase, Search, CheckCircle2, Ban, Send, FileSignature, X,
} from "lucide-react";

const API = "https://api.komorotel.com/api/superadmin";

const euros = (n) => `${Math.round(n || 0).toLocaleString("fr-FR")} €`;
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "—";

/* Une organisation engage Komorotel devant l'hébergeur : si elle ne paie pas,
   c'est nous qui répondons. D'où la validation manuelle et la convention. */
export default function SuperAdminOrganizations() {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [filter, setFilter]   = useState("all");
  const [toValidate, setToValidate] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/organizations`, { withCredentials: true })
      .then(r => setOrganizations(r.data))
      .catch(() => toast.error("Impossible de charger les organisations."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggle = async (o) => {
    let reason = null;
    if (o.active) {
      reason = window.prompt("Motif de la suspension, affiché au gestionnaire :") ;
      if (reason === null) return;
    }
    try {
      await axios.patch(`${API}/organizations/${o._id}/toggle-active`, { reason }, { withCredentials: true });
      toast.success(o.active ? "Organisation suspendue." : "Organisation réactivée.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur.");
    }
  };

  const resend = async (o, purpose) => {
    try {
      const r = await axios.post(`${API}/organizations/${o._id}/resend-activation`, { purpose }, { withCredentials: true });
      toast.success(r.data.message);
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur.");
    }
  };

  const etat = (o) => {
    if (!o.convention?.signed) return { t: "Dossier en attente", c: "bg-amber-50 text-amber-700" };
    if (!o.active)             return { t: "Suspendue",          c: "bg-red-50 text-red-600" };
    return { t: "Active", c: "bg-emerald-50 text-emerald-700" };
  };

  const filtered = organizations.filter(o => {
    if (filter === "pending" && o.convention?.signed) return false;
    if (filter === "active"  && !(o.active && o.convention?.signed)) return false;
    const t = search.toLowerCase().trim();
    if (!t) return true;
    return [o.name, ...(o.managers || []).map(m => m.email)].join(" ").toLowerCase().includes(t);
  });

  const enAttente = organizations.filter(o => !o.convention?.signed).length;

  return (
    <div className="p-6">

      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-medium text-gray-900">Organisations</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            ONG, entreprises et administrations qui réservent pour leurs équipes
          </p>
        </div>
        {enAttente > 0 && (
          <span className="px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 text-xs font-semibold shrink-0">
            {enAttente} dossier{enAttente > 1 ? "s" : ""} à traiter
          </span>
        )}
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl outline-none focus:border-[var(--k-green)] transition"
          />
        </div>
        <div className="flex gap-1">
          {[
            { k: "all",     l: "Toutes" },
            { k: "pending", l: "À traiter" },
            { k: "active",  l: "Actives" },
          ].map(f => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              className={`px-3 py-2 rounded-xl text-sm transition ${
                filter === f.k ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col gap-2">
          {[...Array(3)].map((_, i) => <div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-gray-200 rounded-2xl">
          <Briefcase size={24} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">Aucune organisation.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map(o => {
            const e = etat(o);
            const owner = (o.managers || []).find(m => m.role === "owner") || (o.managers || [])[0];
            return (
              <div key={o._id} className="bg-white border border-gray-100 rounded-2xl p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 flex items-center gap-2">
                      {o.name}
                      <span className={`px-2 py-0.5 rounded-lg text-[11px] font-semibold ${e.c}`}>{e.t}</span>
                    </p>
                    <p className="text-[13px] text-gray-500 mt-0.5">
                      {owner ? `${owner.firstName} ${owner.lastName} · ${owner.email}` : "Aucun gestionnaire"}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Demande du {fmtDate(o.createdAt)}
                      {o.legalId ? ` · N° ${o.legalId}` : ""}
                      {o.convention?.signed ? ` · Convention du ${fmtDate(o.convention.signedAt)}` : ""}
                    </p>
                  </div>

                  <div className="text-right text-[13px] text-gray-500 shrink-0">
                    <p>{o.stats?.members ?? 0} membre{(o.stats?.members ?? 0) > 1 ? "s" : ""}</p>
                    <p>{o.stats?.reservations ?? 0} séjour{(o.stats?.reservations ?? 0) > 1 ? "s" : ""}</p>
                    <p className="font-medium text-gray-900 mt-0.5">{euros(o.stats?.total)}</p>
                    <p className="text-xs text-gray-400">dont {euros(o.stats?.commission)} de commission</p>
                  </div>
                </div>

                {!o.active && o.suspendedReason && (
                  <p className="mt-2 text-[13px] text-red-600">Motif : {o.suspendedReason}</p>
                )}

                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-50">
                  {!o.convention?.signed ? (
                    <button
                      onClick={() => setToValidate(o)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--k-green)] text-white text-sm font-medium hover:bg-[var(--k-green-hover)] transition"
                    >
                      <FileSignature size={15} /> Valider le dossier
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => toggle(o)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition"
                      >
                        {o.active ? <><Ban size={15} /> Suspendre</> : <><CheckCircle2 size={15} /> Réactiver</>}
                      </button>
                      <button
                        onClick={() => resend(o, "activation")}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition"
                      >
                        <Send size={15} /> Renvoyer l'activation
                      </button>
                      <button
                        onClick={() => resend(o, "reset")}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition"
                      >
                        <Send size={15} /> Lien de réinitialisation
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {toValidate && (
        <ValidateDrawer
          organization={toValidate}
          onClose={() => setToValidate(null)}
          onDone={() => { setToValidate(null); load(); }}
        />
      )}
    </div>
  );
}

function ValidateDrawer({ organization, onClose, onDone }) {
  const [form, setForm] = useState({
    legalId: organization.legalId || "",
    reference: "",
    signedAt: new Date().toISOString().slice(0, 10),
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/organizations/${organization._id}/validate`, form, { withCredentials: true });
      toast.success(r.data.message);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur.");
    } finally {
      setLoading(false);
    }
  };

  const champ = (label, k, type = "text") => (
    <div className="flex flex-col gap-1.5" key={k}>
      <label className="text-xs font-medium text-gray-500">{label}</label>
      <input
        type={type}
        value={form[k]}
        onChange={e => setForm(s => ({ ...s, [k]: e.target.value }))}
        className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-[var(--k-green)] transition"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/20" onClick={onClose}>
      <div className="w-full max-w-sm bg-white h-full shadow-xl p-6 overflow-y-auto" onClick={e => e.stopPropagation()}>

        <div className="flex items-start justify-between mb-1">
          <h2 className="text-lg font-medium text-gray-900">Valider le dossier</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 transition">
            <X size={18} />
          </button>
        </div>
        <p className="text-[13px] text-gray-500 mb-5">
          {organization.name}. La validation active le compte et envoie au
          responsable le lien pour choisir son mot de passe.
        </p>

        <div className="mb-5 px-3.5 py-3 rounded-xl bg-amber-50 border border-amber-200">
          <p className="text-[13px] text-amber-900">
            Ne valide que si tu as la convention signée en main. Une organisation
            active peut réserver chez des hébergeurs qui compteront sur nous
            pour être payés.
          </p>
        </div>

        <form onSubmit={submit} className="flex flex-col gap-4">
          {champ("Numéro d'enregistrement", "legalId")}
          {champ("Référence de la convention", "reference")}
          {champ("Date de signature", "signedAt", "date")}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 mt-1 rounded-xl bg-[var(--k-green)] text-white text-sm font-medium hover:bg-[var(--k-green-hover)] transition disabled:opacity-60"
          >
            {loading ? "Validation..." : "Valider et envoyer le lien"}
          </button>
        </form>
      </div>
    </div>
  );
}
